<template>
  <div>
    <div class="debug container">
      <div v-if="debugMode" class="space-1">
        <h5>Query String Parameters</h5>
        <code class="code">
          Lender Id:{{ $route.query.lenderId }}
        </code>
        <h5>Search Query:</h5>
        <code class="code">{{ JSON.stringify(currentSearchQuery) }}</code>
        <h5 class="mt-2">Search Filter:</h5>
        <code>{{ JSON.stringify(searchFilters) }}</code>
      </div>
    </div>
    <HeroSection>
      <h1 v-if="searchQuery.isRemortgage()" class="display-12 text-white font-size-md-down-5 font-weight-semi-bold">
        {{ usingDefaultQuery ? 'Compare' : 'Your' }} <span class="text-warning">Remortgage</span> Deals...
      </h1>
      <p v-if="searchQuery.isRemortgage() && usingDefaultQuery" class="text-white">
        Remortgaging is where you take out a new mortgage with a new lender on a property you already own and have a mortgage on. The new mortgage takes the place of the mortgage you originally had on the property.
      </p>
      <h1 v-if="searchQuery.isFirstTimeBuyer()" class="display-12 text-white font-size-md-down-5 font-weight-semi-bold">
        {{ usingDefaultQuery ? 'Compare' : 'Your' }} <span class="text-warning">First Time Buyer</span> Deals...
      </h1>
      <p v-if="searchQuery.isFirstTimeBuyer() && usingDefaultQuery" class="text-white">
        Buying your first home should be fun – not frightening but it’s easy to see why some people can feel overwhelmed.
        Use our free best buy tool to compare the best mortgage rates for first-time buyers.
      </p>
      <div v-if="usingDefaultQuery" sclass="row">
        <button class="btn btn-success" @click="searchFormOpen = true">Tell us more</button>
      </div>
      <ProductQuerySummary v-if="!usingDefaultQuery" :query="searchQuery" @clicked="searchFormOpen = true"/>
    </HeroSection>
    <div class="bg-light space-1">
      <ProductCardPlaceholder v-if="isSearching" :rows="5" type="media-square" loading align="left"/>
      <div class="container" v-if="!isSearching">
        <ProductNoProductsAvailable 
            v-if="noSearchResultsAvailable"
            @retry="searchFormOpen = true"
            :query="currentSearchQuery"
            :filters="searchFilters" :filtersModified="filtersAreModified"
            :stats="{
              'maxLtv': 0.95
            }"/>
        <div v-if="searchResultsAvailable">
          <div class="row mb-3">
            <transition name="">
              <div v-if="!showFilters && totalResults > 50 && !usingDefaultQuery" class="col">
                <!-- CTA Section -->
                <div class="ww-lg-85 mx-lg-auto">
                  <div class="card bg-secondary text-white overflow-hidden p-4">
                    <div class="row justify-content-md-start align-items-md-center text-center text-md-left">
                      <div class="col-md-7 offset-md-3 mb-3 mb-md-0">
                        <h3 class="text-white mb-1">Confused by all the options?</h3>
                        <p class="text-white mb-0">Filter your results to find the right deals for you.</p>
                      </div>
                      <div class="col-md-2 text-md-right">
                        <button class="btn btn-light" @click="showFilters = true"><i class="fas fa-sliders-h"></i> Show Filters</button>
                      </div>
                    </div>
  
                    <!-- SVG Component -->
                    <figure class="w-25 d-none d-md-block content-centered-y ml-n4">
                      <img class="img-fluid" src="/assets/svg/illustrations/communication.svg" alt="Image Description">
                    </figure>
                    <!-- End SVG Component -->
                  </div>
                </div>
                <!-- End CTA Section -->
              </div>
            </transition>
          </div>
          <!-- Start: Filters Panel -->
          <transition name="">
            <ProductFilters v-if="showFilters" :values="searchFilters" :facets="facets" @closed="showFilters = false" @apply="applyFilters($event)" />
          </transition>
          <!-- End: Filters Panel -->
          <!-- Start: Search Results Header -->
          <div class="row mb-1">
            <div class="col align-content-center text-left ppt-2">
              <ul class="list-inline">
                <li class="list-inline-item"><span class="" style="font-size: large;">Showing <strong>{{ products.length | prettyNumber }}</strong> of <strong>{{ totalResults | prettyNumber }}</strong> products</span></li>
                <li class="list-inline-item"><button class="btn btn-sm btn-outline-secondary" @click="showFilters = !showFilters" title="Show Filters"><i class="fas fa-sliders-h"></i></button></li>
              </ul>
            </div>
            <div class="col align-content-center" align="right">
              <SortByDropdown class="w-50" :value="sortByValue" @selected="sortBySelected($event)"></SortByDropdown>
              <!--                  <button class="btn btn-sm btn-soft-secondary" @click="showFilters = !showFilters"><i class="fas fa-sliders-h"></i> Filters</button>-->
            </div>
          </div>
          <!-- End: Search Results Header -->
          <!-- Start: Search Results -->
          <div class="row bg-light">
            <div v-for="item in products" :key="item.uid" class="mb-5 col-md-12 text-center">
              <ProductCard :item="item" :hide-monthly-cost="usingDefaultQuery" :monthly-comparison="searchQuery.currentMonthly" :query="searchQuery"/>
            </div>
          </div>
          <div v-if="moreSearchResultsAvailable" align="center">
            <button v-if="!isLoadingMore" @click="loadMore()" class="btn btn-soft-primary btn-block">Load More...</button>
            <button v-if="isLoadingMore" class="btn btn-soft-primary btn-block" type="button" disabled>
              <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Loading...
            </button>
          </div>
          <!-- End: Search Results -->
        </div>
      </div>
    </div>
    <ProductQuickSearchModal :show="searchFormOpen" :query="currentSearchQuery" @saved="searchFormUpdated($event)" @closed="searchFormOpen = false"/>
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<script>
  import HeroSection from "@/components/layout/HeroSection";
  import ProductCard from "../components/products/ProductCard";
  import ProductCardPlaceholder from "../components/products/ProductCardPlaceholder";
  import { ProductsApi } from "@/services/ProductsApi.ts";
  import SortByDropdown from "@/components/search/SortByDropdown";
  import ProductQuickSearchModal from "@/components/products/ProductQuickSearchModal";
  import ProductFilters from "@/components/products/ProductFilters";
  import formDefaults from "@/config/searchFormDefaults.json"
  import { ProductSearchQuery } from "@/utils/ProductSearchQuery"
  import ProductQuerySummary from "@/components/products/ProductQuerySummary";
  import ProductNoProductsAvailable from "@/components/products/ProductNoProductsAvailable";
  
  export default {
    name: "Products",
    
    components: {
      HeroSection,
      ProductQuerySummary,
      ProductCard,
      ProductCardPlaceholder,
      SortByDropdown,
      ProductQuickSearchModal,
      ProductFilters,
      ProductNoProductsAvailable
    },

    data() {
      return {
        debugMode: false,
        isSearching: false,
        isLoadingMore: false,
        searchQuery: {},
        searchFilters: formDefaults.filters,
        currentQueryType: null,
        usingDefaultQuery: true,
        searchFormOpen: false,
        sortByValue: "interest-rate",
        sortByQuery: {"field": "initialInterestRate", "order": 1},
        options: {
          rows: 5,
          start: 0,
        },
        totalResults: 0,
        products: [],
        facets: {},
        showFilters: false
      }
    },
    
    watch: {
      $route(to, from) {
        this.setQueryType(this.$route.params.productType);
        if (this.searchQuery.type != null) this.executeSearch();
      },
      currentSearchQuery: {
        deep: true,
        handler() {
          //console.log("currentSearchQuery watched");
        }
      }
    },
    
    created() {
      this.setQueryType(this.$route.params.productType);
    },
    
    mounted() {
      this.executeSearch();
    },
    
    methods: {
      setQueryType(queryType) {
        if (formDefaults.form[queryType] === undefined) {
          console.log("Unknown productType '" + queryType + "' requested");
          this.$router.push({path: "/"});
        }
        else {
          this.currentSearchQuery = new ProductSearchQuery(formDefaults.form[queryType]);
          this.searchFilters = formDefaults.filters;
          this.usingDefaultQuery = true;
        }
      },
      
      applyFilters(newFilters) {
        this.showFilters = false;
        this.searchFilters = { ...newFilters };
        this.executeSearch(0);
      },
      
      sortBySelected(event) {
        this.sortByValue = event.value;
        this.sortByQuery = event.sortBy;
        
        this.executeSearch(0);
      },
      
      loadMore() {
        this.executeSearch(this.products.length);        
      },
      
      searchFormUpdated(queryData) {
        this.currentQueryType = queryData.type;
        this.usingDefaultQuery = false;
        this.searchQuery = new ProductSearchQuery(queryData);
        this.searchFilters = formDefaults.filters;
        this.executeSearch();
      },

      executeSearch(start) {
        this.isSearching = start === undefined || start === null || start === 0;
        this.isLoadingMore = !(start === null || start === undefined);
        let client = new ProductsApi("https://localhost:5001/api/1.0");
        client.search(this.searchQuery.type, this.searchQuery.borrowingAmount(), this.searchQuery.propertyValue, this.searchQuery.term, start, this.options.rows, this.sortByQuery, this.searchFilters)
            .then(products => {
              if (start !== undefined && start !== 0) {
                this.products = this.products.concat(products.results);
              } else {
                this.facets = products.facets;
                this.products = products.results;
              }
              this.totalResults = products.totalResults;
              this.isSearching = false;
              this.isLoadingMore = false;
            })
            .catch(error => {
              this.isSearching = false;
              this.isLoadingMore = false;
              this.products = null;
            });
      },
    },
    
    computed: {
      filtersAreModified() {
        return JSON.stringify(formDefaults.filters) !== JSON.stringify(this.searchFilters);
      },
      
      currentSearchQuery: {
        get() {
          return this.searchQuery;
        },
        set(value) {
          this.searchQuery = value;
        }
      },
      
      isFailedSearch() {
        return this.products == null;
      },
      
      noSearchResultsAvailable() {
        return this.products != null && this.products.length === 0;
      },

      searchResultsAvailable() {
        return this.products != null && this.products.length > 0;
      },
      
      moreSearchResultsAvailable() {
        return this.products.length < this.totalResults;
      }
    }
  };
</script>
